import { createSignal, type JSX } from 'solid-js';
import { t } from '../i18n';
import { createElementWidth } from '../lib/element-width';
import { type FleetLiveModel, itemStateText, itemWhereText } from '../lib/fleet-live';
import { deckGeometry, type OverflowPill, type PlacedPill, pillAt } from '../lib/fleet-live-deck';
import { createDeckPainter } from '../lib/fleet-live-deck-paint';
import type { LiveLoop } from '../lib/fleet-live-loop';
import { FleetLiveCanvas } from './FleetLiveCanvas';
import { FleetLiveLegend, type LegendEntry } from './FleetLiveLegend';
import { FleetLiveLog } from './FleetLiveLog';
import { FleetLiveOverflow } from './FleetLiveOverflow';
import { FleetLiveText } from './FleetLiveText';
import { FleetLiveTip, type TipState } from './FleetLiveTip';

// The Command Deck's body (#6778, prototype `:3505-3510`): the canvas beside a 280 px event
// log, the log stacking under the canvas (capped at 240 px) below 1,100 px of container
// width, measured here rather than by a container query (the design system has none), then
// the legend with the fold line. Hover a pill for its tooltip. A crowded lane's "+N" pill
// is a real button over the canvas (`FleetLiveOverflow`), re-rendered only when a lane's
// overflow changes, never per frame.

/** The log's column, and the container width below which it stacks. */
export const LOG_COLUMN_PX = 280;
export const STACK_BELOW_PX = 1100;

const LEGEND: readonly { tone: LegendEntry['tone']; key: () => string }[] = [
  { tone: 'live', key: () => t('fleet.live.legend_running') },
  { tone: 'parked', key: () => t('fleet.live.legend_parked') },
  { tone: 'red', key: () => t('fleet.live.legend_blocked') },
  { tone: 'shipped', key: () => t('fleet.live.legend_merged') },
];

export function FleetLiveDeck(props: {
  readonly model: FleetLiveModel;
  readonly loop: LiveLoop;
}): JSX.Element {
  const body = createElementWidth(() => props.loop.invalidate());
  const painter = createDeckPainter();
  const [hover, setHover] = createSignal<string | null>(null);
  const [tip, setTip] = createSignal<TipState | null>(null);
  let pills: readonly PlacedPill[] = [];
  const [overflow, setOverflow] = createSignal<readonly OverflowPill[]>([]);
  let overflowSig = '';
  const noteOverflow = (next: readonly OverflowPill[]): void => {
    const sig = next
      .map((o) => `${o.key}|${o.x}|${o.y}|${o.w}|${o.hidden.map((i) => i.id).join(',')}`)
      .join(';');
    if (sig === overflowSig) return;
    overflowSig = sig;
    setOverflow(next);
  };
  const hoverOverflow = (key: string | null): void => {
    setHover(key);
    props.loop.invalidate();
  };
  const stacked = (): boolean => body.width() < STACK_BELOW_PX;
  const canvasWidth = (): number => (stacked() ? body.width() : body.width() - LOG_COLUMN_PX);
  const height = (w: number): number => deckGeometry(w, props.model.machines.length).h + 8;

  const onMove = (x: number, y: number, e: PointerEvent): void => {
    const hit = pillAt(pills, x, y);
    const id = hit?.item.id ?? null;
    if (id !== hover()) {
      setHover(id);
      props.loop.invalidate();
    }
    setTip(
      hit === null
        ? null
        : {
            x: e.clientX,
            y: e.clientY,
            title: hit.item.label,
            state: itemStateText(hit.item),
            where: itemWhereText(hit.item, Date.now()),
          },
    );
  };
  const onLeave = (): void => {
    setHover(null);
    setTip(null);
    props.loop.invalidate();
  };

  return (
    <>
      <div
        class="fleet-live__deck-body"
        classList={{ 'fleet-live__deck-body--stacked': stacked() }}
        ref={(el) => body.attach(el)}
      >
        <div class="fleet-live__canvas-wrap">
          <FleetLiveCanvas
            label={t('fleet.live.deck_canvas')}
            loop={props.loop}
            width={canvasWidth}
            height={height}
            paint={(ctx, p, w, f) => {
              const drawn = painter.paint(ctx, {
                g: deckGeometry(w, props.model.machines.length),
                p,
                model: props.model,
                motion: f.motion,
                now: f.now,
                dt: f.dt,
                hover: hover(),
              });
              pills = drawn.pills;
              noteOverflow(drawn.overflow);
            }}
            onPointerMove={onMove}
            onPointerLeave={onLeave}
          />
          <FleetLiveOverflow overflow={overflow()} onTip={setTip} onHover={hoverOverflow} />
          <FleetLiveText model={props.model} />
        </div>
        <FleetLiveLog
          lines={props.model.events}
          height={stacked() || canvasWidth() <= 0 ? null : height(canvasWidth())}
        />
      </div>
      <FleetLiveTip tip={tip()} />
      <FleetLiveLegend
        entries={LEGEND.map((e) => ({ tone: e.tone, label: e.key() }))}
        fold={props.model.fold}
      />
    </>
  );
}
